import {
  Stack,
  Dropdown,
  H5,
  Inline,
  Input,
  P,
  Radio,
  TextArea,
  Combobox,
  Spinner,
  Box,
  Grid
} from "@adfenix/alexandria";
import { ProgramService } from "@adfenix/types";
import { useFormContext, Controller } from "react-hook-form";
import { useEffect, useState } from "react";
import {
  BundleFormKey,
  CustomFormKey,
  EmailsNotificationFormKey,
  TicketsNotificationFormKey
} from "../util/formKey";
import { useQuery } from "@tanstack/react-query";
import { SRegularTrashCan } from "@adfenix/icons";
import { IconButton } from "components/IconButton/IconButton";
import { fromTimeSpan, toTimeSpan } from "utils/timeSpan";
import { useTranslation } from "react-i18next";
import { formMaxWidth } from "../../bundle";
import { EmailServiceApi } from "api/services";

type Props = {
  emails: ProgramService.ProgramEmailModel[];
  tickets: ProgramService.ProgramTicketModel[];
  baseKey: CustomFormKey | BundleFormKey;
} & ConditionalProps;

type ConditionalProps =
  | {
      type: "email";
      notificationKey: `${EmailsNotificationFormKey}.${number}`;
      notification: ProgramService.ProgramEmailModel;
    }
  | {
      type: "ticket";
      notificationKey: `${TicketsNotificationFormKey}.${number}`;
      notification: ProgramService.ProgramTicketModel;
    };

const {
  emailType: { authV2EmailTypeAvailableList }
} = EmailServiceApi;

export const Notification = ({
  notification,
  type,
  notificationKey,
  emails,
  tickets,
  baseKey
}: Props) => {
  const { t } = useTranslation();

  const { setValue, register, control, watch } =
    useFormContext<ProgramService.ProgramModel>();

  const [delayUnit, setDelayUnit] = useState<"minutes" | "hours" | "days">(
    (Object.entries(fromTimeSpan(notification.delayBeforeSending)).find(
      ([_, value]) => value > 0
    )?.[0] as "minutes" | "hours" | "days") ?? "hours"
  );
  const [delayValue, setDelayValue] = useState(
    fromTimeSpan(notification.delayBeforeSending)[delayUnit]
  );

  const publishingMode = watch("publishingMode");

  const { data: emailTypes } = useQuery(
    ["getAllEmailTypes"],
    () => authV2EmailTypeAvailableList(),
    {
      enabled: type === "email"
    }
  );
  const emailTypesOnProgramInstance = emailTypes?.filter(
    emailType => emailType.type === "programInstance"
  );

  useEffect(() => {
    setValue(
      `${notificationKey}.delayBeforeSending`,
      toTimeSpan({
        [delayUnit]: delayValue
      })
    );
  }, [delayUnit, delayValue, notificationKey, setValue]);

  if (type === "email" && !emailTypes) {
    return <Spinner />;
  }

  return (
    <Stack spacing="l">
      <Stack spacing="xs">
        <Box display="flex" justifyContent="space-between">
          <H5>
            {t("programBuilder.program.template.campaign.notification.header")}
          </H5>

          <IconButton
            label="delete-notification"
            onClick={() => {
              if (type === "email") {
                setValue(
                  `${baseKey}.emails`,
                  emails.filter(x => x.id !== notification.id)
                );
              } else {
                setValue(
                  `${baseKey}.tickets`,
                  tickets.filter(x => x.id !== notification.id)
                );
              }
            }}
          >
            <SRegularTrashCan />
          </IconButton>
        </Box>

        <P>{t("programBuilder.program.template.campaign.notification.kind")}</P>

        <Inline gap="l">
          <Radio
            label={t(
              "programBuilder.program.template.campaign.notification.zendesk.label"
            )}
            id={`${baseKey}-ticket-${notification.id}`}
            checked={type === "ticket"}
            value="ticket"
            onChange={() => {
              const updatedEmails = emails.filter(
                x => x.id !== notification.id
              );

              const updatedTickets = [...tickets, { ...notification }];

              setValue(`${baseKey}.emails`, updatedEmails);
              setValue(`${baseKey}.tickets`, updatedTickets);
            }}
          />
          <Radio
            checked={type === "email"}
            label={t(
              "programBuilder.program.template.campaign.notification.email.label"
            )}
            value="email"
            id={`${baseKey}-email-${notification.id}`}
            onChange={() => {
              const updatedTickets = tickets.filter(
                x => x.id !== notification.id
              );

              const updatedEmails = [
                ...emails,
                {
                  ...notification,
                  // The only difference between the two models is the emailType.
                  // Instead of removing the selected emailType when changing to ticket
                  // we keep it even on the ticket model so that we can use it if switching
                  // back to email.
                  emailType:
                    "emailType" in notification ? notification.emailType : ""
                }
              ];

              setValue(`${baseKey}.tickets`, updatedTickets);
              setValue(`${baseKey}.emails`, updatedEmails);
            }}
          />
        </Inline>
      </Stack>

      <Stack spacing="xs">
        <P>{t("programBuilder.program.template.campaign.notification.when")}</P>

        <Grid
          columns={["1fr", "1fr 1fr"]}
          gap="l"
          style={{ maxWidth: formMaxWidth }}
        >
          <Dropdown
            id={`${notificationKey}.delayUntilSubjectPassesPublicationLimitation`}
            label={t(
              "programBuilder.program.template.campaign.notification.event.label"
            )}
            style={{ minWidth: "150px" }}
            items={[
              t(
                "programBuilder.program.template.campaign.notification.event.onOrder"
              ),
              t(
                "programBuilder.program.template.campaign.notification.event.onForSale"
              )
            ]}
            onChange={val => {
              const delay =
                val ===
                t(
                  "programBuilder.program.template.campaign.notification.event.onForSale"
                );

              setValue(
                `${notificationKey}.delayUntilSubjectPassesPublicationLimitation`,
                delay
              );
            }}
            defaultValue={
              notification.delayUntilSubjectPassesPublicationLimitation
                ? t(
                    "programBuilder.program.template.campaign.notification.event.onForSale"
                  )
                : t(
                    "programBuilder.program.template.campaign.notification.event.onOrder"
                  )
            }
            disabled={publishingMode !== "SellerPaid"}
          />

          <Box display="flex" justifyContent="space-between" alignItems="end">
            <div style={{ flex: 1, paddingRight: 10 }}>
              <Input
                type="number"
                label={t(
                  t(
                    "programBuilder.program.template.campaign.notification.delay.label"
                  )
                )}
                data-testid={`${notificationKey}-delayBeforeSending`}
                onChange={e => {
                  setDelayValue(Number(e.target.value));
                }}
                defaultValue={delayValue}
              />
            </div>

            <Dropdown
              data-testid={`${notificationKey}-delayBeforeSendingUnit`}
              items={["minutes", "hours", "days"]}
              onChange={val => {
                setDelayUnit(val);
              }}
              defaultValue={delayUnit}
            />
          </Box>
          {type === "email" && (
            <Controller
              control={control}
              name={`${notificationKey}.emailType`}
              render={({ field }) => (
                <Combobox
                  placeholder={t(
                    "programBuilder.program.template.campaign.notification.email.placeholder"
                  )}
                  items={emailTypesOnProgramInstance ?? []}
                  valueObjectIndexAccessor="name"
                  onChange={email => field.onChange(email.name)}
                  classNamePrefix={`${notificationKey}-emailType`}
                  label={t(
                    "programBuilder.program.template.campaign.notification.emailType.label"
                  )}
                  defaultItem={
                    "emailType" in notification
                      ? emailTypesOnProgramInstance?.find(
                          x => x.name === notification.emailType
                        )
                      : undefined
                  }
                />
              )}
            />
          )}
          <div style={{ gridColumn: "span 2" }}>
            <TextArea
              style={{ maxWidth: "100%" }}
              {...register(`${notificationKey}.message`)}
              data-testid={`${notificationKey}-message`}
              label={
                type === "email"
                  ? t(
                      "programBuilder.program.template.campaign.notification.messageEmail"
                    )
                  : t(
                      "programBuilder.program.template.campaign.notification.messageInternal"
                    )
              }
            />
          </div>
        </Grid>
      </Stack>
    </Stack>
  );
};
